<template>
  <!--
    Admin のリッチテキストエディタで作成された質問文を描画する。入力元は Admin のみで、
    保存時に許可タグへ絞られているため信頼済みコンテンツとして v-html で描画する。
    Tailwind preflight が見出し・箇条書きの既定スタイルを消すため、ここで明示的に補う。
  -->
  <!-- eslint-disable-next-line vue/no-v-html -->
  <div :class="['rich-text-label', { 'rich-text-label--required': required }]" v-html="blockHtml" />
</template>

<script setup lang="ts">
const props = withDefaults(
  defineProps<{
    /** Admin の保存時にサニタイズ済みの質問文だけを渡す。ゲスト入力や外部由来の HTML を渡さないこと */
    html: string;
    /** 必須マークを質問文の文末に付ける。読み上げ用の文言は呼び出し元が別に置く */
    required?: boolean;
  }>(),
  { required: false },
);

// Quill は末尾で改行すると <p><br></p> を残す。そのまま描くと必須マークが空行だけの最終ブロックに落ちる
const trimmedHtml = computed(() => props.html.replace(/(?:<p>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>\s*)+$/gi, ''));

// 必須マークは最終ブロックの擬似要素で出すため、装飾のないプレーンテキストの label も段落に包む。
// :has() で「ブロックを持たない場合」を拾うと、非対応ブラウザで全フォームのマークが消える
const blockHtml = computed(() => (/<(p|h[1-6]|ul|ol|div|blockquote)[\s/>]/i.test(trimmedHtml.value) ? trimmedHtml.value : `<p>${trimmedHtml.value}</p>`));
</script>

<style scoped>
/*
  質問文に許可している装飾（見出し h2/h3・太字・箇条書き・リンク）だけを対象にする。
  本文の色・サイズは呼び出し元から継承させ、ここでは強弱と行の区切りだけを決める。
*/
.rich-text-label {
  /* 折り返しのない長い URL がスマートフォン幅で横スクロールを起こさないようにする */
  overflow-wrap: anywhere;
}

/*
  必須マークは質問文の「最後の行の文末」に出す。
  兄弟要素として並べると、質問文が見出しや箇条書きで終わったときにマークだけが
  コンテンツ幅の右端へ飛ぶ（最終行ではなく最長行の右に付く）ため、
  末尾のブロックの中に擬似要素として差す。読み上げ用の文言は呼び出し元が別に置く。
*/
.rich-text-label--required > :deep(:is(p, h1, h2, h3, h4, h5, h6, div, blockquote):last-child)::after,
.rich-text-label--required > :deep(:is(ul, ol):last-child > li:last-child)::after {
  content: ' *';
}

.rich-text-label :deep(p) {
  margin: 0;
}

.rich-text-label :deep(p + p) {
  margin-top: 0.5em;
}

.rich-text-label :deep(h2),
.rich-text-label :deep(h3) {
  font-weight: 600;
  line-height: 1.4;
}

.rich-text-label :deep(h2) {
  font-size: 1.125em;
}

.rich-text-label :deep(h3) {
  font-size: 1em;
}

.rich-text-label :deep(:is(h2, h3):not(:first-child)) {
  margin-top: 0.75em;
}

.rich-text-label :deep(strong),
.rich-text-label :deep(b) {
  font-weight: 600;
}

/*
  Quill 2 は箇条書きも番号付きも <ol> で出し、種別は li の data-list で表す。
  コンテナのタグでマーカーを決めると箇条書きが「1.」になるため、data-list を持つ項目は
  マーカーを自前で描く。番号は番号付きの項目だけで進める（Admin のエディタ上の見え方と揃える）。
  data-list を持たない素の <ul> / <ol> は、既定のマーカーのまま出す。
*/
.rich-text-label :deep(ul),
.rich-text-label :deep(ol) {
  margin: 0.25em 0;
  /* 2em は 2 桁の番号（10. 以降）がリストの外へはみ出さない幅 */
  padding-left: 2em;
  counter-reset: rich-text-label-item;
}

.rich-text-label :deep(ul) {
  list-style: disc;
}

.rich-text-label :deep(ol) {
  list-style: decimal;
}

.rich-text-label :deep(li) {
  margin: 0.125em 0;
}

.rich-text-label :deep(li[data-list]) {
  position: relative;
  list-style-type: none;
}

/* Quill の list format は checked / unchecked も通す。既定を中黒にして、種別が不明でもマーカーを消さない */
.rich-text-label :deep(li[data-list])::before {
  content: '•';
  position: absolute;
  right: 100%;
  padding-right: 0.5em;
}

.rich-text-label :deep(li[data-list='ordered']) {
  counter-increment: rich-text-label-item;
}

.rich-text-label :deep(li[data-list='ordered'])::before {
  content: counter(rich-text-label-item) '. ';
  padding-right: 0.25em;
  white-space: nowrap;
}

/* エディタがマーカーを描くための要素。保存時に落としているが、混じっても本文には出さない */
.rich-text-label :deep(.ql-ui) {
  display: none;
}

.rich-text-label :deep(a) {
  @apply text-gold3 underline;
}

/* 画像は装飾セットに含めていないが、過去データが混じっても画面幅を超えないようにする */
.rich-text-label :deep(img) {
  max-width: 100%;
  height: auto;
}
</style>
